import React from 'react';

import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import SettingsInputComponentOutlinedIcon from '@mui/icons-material/SettingsInputComponentOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';

type ServicesPrecisionControlProps = {
  onBack: () => void;
};

export default function ServicesPrecisionControl({ onBack }: ServicesPrecisionControlProps): React.ReactElement {
  return (
    <section className="bg-slate-50">
      <div className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-20">
        <div className="mx-auto max-w-5xl">
          <button
            onClick={onBack}
            className="mb-6 flex items-center gap-2 text-sky-600 hover:text-sky-700 transition-colors"
          >
            <ArrowBackOutlinedIcon fontSize="small" />
            <span className="text-sm font-semibold">Back to Services</span>
          </button>
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-sky-600 text-white text-sm font-semibold mb-4">
              Service One
            </span>
            <h2 className="text-3xl font-semibold leading-tight text-slate-900 sm:text-4xl">
              Precision Control for Modern Industry
            </h2>
            <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg max-w-3xl mx-auto">
              We design, implement, and maintain advanced control systems that transform manual processes
              into high-efficiency automated operations.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-3 mb-10">
            {/* Custom System Design Card */}
            <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                  <SettingsInputComponentOutlinedIcon fontSize="medium" />
                </div>
                <h4 className="text-lg font-semibold text-slate-900">Custom System Design</h4>
              </div>
              <ul className="space-y-2.5 text-sm leading-relaxed text-slate-600">
                <li className="flex items-start">
                  <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                  <span>
                    <strong className="font-semibold text-slate-900">Thermal Management:</strong> Specialized
                    control for furnaces and boilers.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                  <span>
                    <strong className="font-semibold text-slate-900">Fluid Dynamics:</strong> High-precision
                    pressure control systems.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                  <span>
                    <strong className="font-semibold text-slate-900">Process Conditioning:</strong> Humidity
                    and moisture control for maize mill plants and food processing.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                  <span>
                    <strong className="font-semibold text-slate-900">Electronic Circuitry:</strong> In-house
                    specialized electronic circuit design.
                  </span>
                </li>
              </ul>
            </div>

            {/* Programming & System Integration Card */}
            <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                  <CodeOutlinedIcon fontSize="medium" />
                </div>
                <h4 className="text-lg font-semibold text-slate-900">Programming & Integration</h4>
              </div>
              <ul className="space-y-2.5 text-sm leading-relaxed text-slate-600">
                <li className="flex items-start">
                  <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                  <span>
                    Expert PLC programming and routine servicing.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                  <span>
                    SCADA software implementation for real-time plant monitoring.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                  <span>
                    Seamless integration of new hardware into legacy systems.
                  </span>
                </li>
              </ul>
            </div>

            {/* Diagnostic & Maintenance Services Card */}
            <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                  <BuildOutlinedIcon fontSize="medium" />
                </div>
                <h4 className="text-lg font-semibold text-slate-900">Diagnostic & Maintenance</h4>
              </div>
              <ul className="space-y-2.5 text-sm leading-relaxed text-slate-600">
                <li className="flex items-start">
                  <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                  <span>
                    <strong className="font-semibold text-slate-900">Industrial Troubleshooting:</strong> Root-Cause-Analysis (RCA) to identify underlying issues.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                  <span>
                    <strong className="font-semibold text-slate-900">Component Repair:</strong> Control Cards, PSUs, and VSDs.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                  <span>
                    <strong className="font-semibold text-slate-900">Instrumentation:</strong> Precision calibration and industrial thermography.
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* The Ancient Engineering Advantage */}
          <div className="rounded-2xl bg-sky-600 p-8 lg:p-10 text-center">
            <h4 className="text-2xl font-semibold text-white mb-4">The Ancient Engineering Advantage</h4>
            <p className="text-base leading-relaxed text-white/90 max-w-2xl mx-auto">
              We reduce your downtime by identifying the root cause of electrical and automation failures
              rather than just treating the symptoms.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}


